import { cn } from '@/lib/utils';
import { RAGStatus, getRAGStatus, designTokens } from '@/lib/theme';

interface RAGBadgeProps {
  status: RAGStatus;
  label?: string;
  showDot?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const sizeStyles = {
  sm: {
    badge: 'px-1.5 py-0.5 text-xs',
    dot: 'h-1.5 w-1.5',
  },
  md: {
    badge: 'px-2 py-0.5 text-xs',
    dot: 'h-2 w-2',
  },
  lg: {
    badge: 'px-2.5 py-1 text-sm',
    dot: 'h-2.5 w-2.5',
  },
};

const statusLabels: Record<RAGStatus, string> = {
  green: 'Good',
  amber: 'Warning',
  red: 'Critical',
};

const statusBackgrounds: Record<RAGStatus, string> = {
  green: 'bg-green-100 text-green-800',
  amber: 'bg-amber-100 text-amber-800',
  red: 'bg-red-100 text-red-800',
};

const dotBackgrounds: Record<RAGStatus, string> = {
  green: 'bg-green-500',
  amber: 'bg-amber-500',
  red: 'bg-red-500',
};

export function RAGBadge({
  status,
  label,
  showDot = true,
  size = 'md',
  className,
}: RAGBadgeProps) {
  const displayLabel = label ?? statusLabels[status];
  const styles = sizeStyles[size];

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full font-medium',
        statusBackgrounds[status],
        styles.badge,
        className
      )}
    >
      {showDot && (
        <span className={cn('rounded-full', dotBackgrounds[status], styles.dot)} />
      )}
      {displayLabel}
    </span>
  );
}

interface RAGDotProps {
  status: RAGStatus;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export function RAGDot({ status, size = 'md', className }: RAGDotProps) {
  const styles = sizeStyles[size];

  return (
    <span
      className={cn('inline-block rounded-full', dotBackgrounds[status], styles.dot, className)}
      style={{ backgroundColor: designTokens.rag[status] }}
      aria-label={statusLabels[status]}
    />
  );
}

interface RAGIndicatorProps {
  percentage: number;
  label?: string;
  showDot?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export function RAGIndicator({
  percentage,
  label,
  showDot = true,
  size = 'md',
  className,
}: RAGIndicatorProps) {
  const status = getRAGStatus(percentage);
  return (
    <RAGBadge
      status={status}
      label={label}
      showDot={showDot}
      size={size}
      className={className}
    />
  );
}

export default RAGBadge;
